/**
 * LiveAISPanel — the click-to-expand dashboard that lives behind the
 * vessel-count badge in the bottom-left of the map.
 *
 * The badge itself stays small and unobtrusive (one line, no chrome
 * change). A tiny chevron hints that it's interactive without
 * demanding attention. Clicking opens this panel above the badge with
 * a focused set of live signals — chosen because they're useful to a
 * marine inspector + obvious from the data we already have on hand:
 *
 *   1. Vessel-kind mix       — what's on the map right now, by class
 *   2. Activity state        — anchored / manoeuvring / underway
 *   3. AIS feed freshness    — newest frame age + position count
 *   4. Live update tape      — how many positions have rolled in since
 *                              the panel opened (proves it's *live*)
 *   5. "What you can do"     — search a vessel, narrow with filters
 *
 * Numbers update on every poll (every ~30 s in the parent), so
 * leaving the panel open lets the inspector watch the feed move.
 *
 * Pure client-side computation — every number is derived from the
 * `ships` array passed in. No additional API call needed.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  Anchor,
  ChevronDown,
  Compass,
  Cpu,
  Database,
  Globe2,
  Layers,
  Radio,
  Ship,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import type { Ship as ShipType } from '../../types/ship';

interface Props {
  /** Full ships dataset (post-fetch, pre-filter). */
  ships: ShipType[];
  /** Filtered subset currently rendered on the map. */
  filteredShips: ShipType[];
  /** Newest AIS frame age in seconds, from /api/ships envelope. */
  newestAgeSec: number | null;
  /** Whether the data is from the live feed or the fallback fixture. */
  isLive: boolean;
  /** Close-callback wired to the parent. */
  onClose: () => void;
}

/* Same colour vocabulary as the map dots, so what the inspector sees
 * in the panel maps 1:1 to what they see on the canvas. Mirrors
 * MapPage's KIND palette. */
const KIND_META: Array<{
  id: string;
  label: string;
  hex: string;
  glyph: string;
}> = [
  { id: 'tanker',    label: 'Tankers',    hex: '#06B6D4', glyph: '◣' },
  { id: 'cargo',     label: 'Cargo',      hex: '#10B981', glyph: '■' },
  { id: 'container', label: 'Containers', hex: '#3B82F6', glyph: '▥' },
  { id: 'passenger', label: 'Passenger',  hex: '#F59E0B', glyph: '★' },
  { id: 'fishing',   label: 'Fishing',    hex: '#A78BFA', glyph: '⏃' },
  { id: 'other',     label: 'Other',      hex: '#94A3B8', glyph: '●' },
];

function kindFor(typeRaw: string | undefined): string {
  const t = (typeRaw ?? '').toLowerCase();
  if (!t) return 'other';
  if (t.includes('tanker') || t.includes('chem') || t.includes('lpg') || t.includes('lng') || t.includes('gas')) return 'tanker';
  if (t.includes('container'))                                     return 'container';
  if (t.includes('passenger') || t.includes('cruise') || t.includes('ferry') || t.includes('roro')) return 'passenger';
  if (t.includes('fish'))                                          return 'fishing';
  if (t.includes('cargo') || t.includes('bulk') || t.includes('general')) return 'cargo';
  return 'other';
}

export default function LiveAISPanel({
  ships,
  filteredShips,
  newestAgeSec,
  isLive,
  onClose,
}: Props) {
  // Esc closes; click-outside closes via the backdrop.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ── Aggregations ────────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = ships.length;
    const visible = filteredShips.length;

    // Vessel-kind mix
    const byKind: Record<string, number> = {};
    for (const s of ships) {
      const k = kindFor(s.type);
      byKind[k] = (byKind[k] ?? 0) + 1;
    }

    // Activity state — speed buckets
    let anchored = 0, manoeuv = 0, underway = 0, noSpeed = 0;
    for (const s of ships) {
      const v = s.speed;
      if (v == null) noSpeed++;
      else if (v < 1) anchored++;
      else if (v < 5) manoeuv++;
      else underway++;
    }

    return { total, visible, byKind, anchored, manoeuv, underway, noSpeed };
  }, [ships, filteredShips]);

  // ── Live update tape ────────────────────────────────────────────
  // Tracks vessel set deltas since the panel opened so the user can
  // watch the feed move. Set membership is keyed by mmsi for stability
  // across polls.
  const initialMmsis = useRef<Set<string>>(new Set());
  const [newCount, setNewCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const lastSnapshotRef = useRef<Map<string, number>>(new Map()); // mmsi -> ts (epoch ms)

  useEffect(() => {
    if (initialMmsis.current.size === 0) {
      // First mount: snapshot the current set as the baseline.
      ships.forEach((s) => {
        if (s.mmsi) initialMmsis.current.add(s.mmsi);
        if (s.mmsi) {
          lastSnapshotRef.current.set(
            s.mmsi,
            s.timestamp ? +new Date(s.timestamp) : 0,
          );
        }
      });
      return;
    }
    let added = 0;
    let updated = 0;
    for (const s of ships) {
      if (!s.mmsi) continue;
      if (!initialMmsis.current.has(s.mmsi)) {
        added++;
        initialMmsis.current.add(s.mmsi);
      }
      const prevTs = lastSnapshotRef.current.get(s.mmsi) ?? 0;
      const curTs = s.timestamp ? +new Date(s.timestamp) : 0;
      if (curTs > prevTs) {
        updated++;
        lastSnapshotRef.current.set(s.mmsi, curTs);
      }
    }
    if (added > 0)   setNewCount((n) => n + added);
    if (updated > 0) setUpdatedCount((n) => n + updated);
  }, [ships]);

  // ── Render helpers ──────────────────────────────────────────────
  const ageLabel = (() => {
    if (!isLive) return 'sample data';
    if (newestAgeSec == null) return '—';
    if (newestAgeSec < 60) return `${newestAgeSec}s ago`;
    if (newestAgeSec < 3600) return `${Math.floor(newestAgeSec / 60)} min ago`;
    return `${Math.floor(newestAgeSec / 3600)} h ago`;
  })();
  const ageDot = (() => {
    if (!isLive)               return 'bg-red-400';
    if (newestAgeSec == null)  return 'bg-slate-500';
    if (newestAgeSec < 600)    return 'bg-emerald-400 animate-pulse';
    if (newestAgeSec < 7200)   return 'bg-cyan-400';
    return 'bg-amber-400';
  })();

  const sumKind = stats.total || 1;
  const speedSum = stats.anchored + stats.manoeuv + stats.underway || 1;

  return (
    <>
      {/* Backdrop — click-outside dismiss. Transparent so the map stays
          visible behind it; the user's mental model is "panel above the
          badge", not "modal overlay". */}
      <div
        className="absolute inset-0 z-30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* The panel itself, anchored above the bottom-left badge. */}
      <div
        role="dialog"
        aria-label="Live AIS dashboard"
        className="absolute bottom-20 left-4 z-40 w-[320px] rounded-xl border border-cyan-500/30 bg-slate-950/95 backdrop-blur-md text-slate-100 shadow-2xl overflow-hidden animate-[panel-in_220ms_cubic-bezier(0.2,0.8,0.2,1)]"
        style={{ boxShadow: '0 24px 60px -25px rgba(0,0,0,0.7), 0 0 0 1px rgba(6,182,212,0.10) inset' }}
      >
        {/* Header */}
        <div className="px-4 py-2.5 border-b border-slate-800 flex items-center gap-2">
          <Radio className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
          <span className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">
            Live AIS dashboard
          </span>
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-300">
            <span className={`w-1.5 h-1.5 rounded-full ${ageDot}`} aria-hidden="true" />
            <span>{ageLabel}</span>
          </span>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-500 hover:text-white shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Big number row */}
        <div className="px-4 pt-3 pb-2 grid grid-cols-2 gap-2">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Tracking now</div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-extrabold text-white tabular-nums">{stats.total.toLocaleString()}</span>
              <span className="text-[10px] text-slate-400">vessels</span>
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">In view</div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-extrabold text-cyan-300 tabular-nums">{stats.visible.toLocaleString()}</span>
              <span className="text-[10px] text-slate-400">
                {stats.total > 0 ? Math.round((stats.visible / stats.total) * 100) : 0}%
              </span>
            </div>
          </div>
        </div>

        {/* Vessel-mix bar — colours match map dots, so the inspector
            can read this back to the canvas without translating. */}
        <div className="px-4 pt-2 pb-3">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
            Vessel mix
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-slate-800">
            {KIND_META.map((k) => {
              const n = stats.byKind[k.id] ?? 0;
              const pct = (n / sumKind) * 100;
              if (n === 0) return null;
              return (
                <div
                  key={k.id}
                  title={`${k.label}: ${n}`}
                  style={{ width: `${pct}%`, background: k.hex }}
                />
              );
            })}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
            {KIND_META.map((k) => {
              const n = stats.byKind[k.id] ?? 0;
              if (n === 0) return null;
              return (
                <div key={k.id} className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: k.hex }}
                    aria-hidden="true"
                  />
                  <span className="text-slate-300 truncate">{k.label}</span>
                  <span className="ml-auto text-slate-100 font-mono tabular-nums">{n.toLocaleString()}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Activity state */}
        <div className="px-4 py-3 border-t border-slate-800/80">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mb-2">
            Activity state
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-slate-800">
            <div style={{ width: `${(stats.anchored / speedSum) * 100}%`, background: '#64748B' }} />
            <div style={{ width: `${(stats.manoeuv / speedSum) * 100}%`, background: '#06B6D4' }} />
            <div style={{ width: `${(stats.underway / speedSum) * 100}%`, background: '#10B981' }} />
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
            <ActivityCell Icon={Anchor}  tint="text-slate-300"   label="Anchored"   sub="< 1 kn"    n={stats.anchored} />
            <ActivityCell Icon={Compass} tint="text-cyan-300"    label="Manoeuv."   sub="1–5 kn"    n={stats.manoeuv} />
            <ActivityCell Icon={Zap}     tint="text-emerald-300" label="Underway"   sub="≥ 5 kn"    n={stats.underway} />
          </div>
        </div>

        {/* Live update tape */}
        <div className="px-4 py-3 border-t border-slate-800/80">
          <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">
            Since you opened this panel
          </div>
          <div className="flex items-center gap-3 text-[12px]">
            <div className="flex items-center gap-1.5">
              <Activity className="w-3.5 h-3.5 text-emerald-300" />
              <span className="text-emerald-300 font-semibold tabular-nums">+{updatedCount}</span>
              <span className="text-slate-400">positions updated</span>
            </div>
            {newCount > 0 && (
              <div className="flex items-center gap-1.5 ml-auto">
                <Ship className="w-3.5 h-3.5 text-cyan-300" />
                <span className="text-cyan-300 font-semibold tabular-nums">+{newCount}</span>
                <span className="text-slate-400">new vessels</span>
              </div>
            )}
          </div>
        </div>

        {/* Under the hood — collapsible platform capabilities. Conveys
            the sophistication of what's running underneath without
            naming vendors, models, or data providers. Closed by default
            so the dashboard stays focused; one click to expand. */}
        <UnderTheHood />

        {/* Footer */}
        <div className="px-4 py-2 border-t border-slate-800/80 bg-slate-900/40">
          <div className="flex items-center gap-2 text-[10px] text-slate-500">
            <Database className="w-3 h-3 shrink-0" />
            <span>Live AIS feed · refreshed continuously</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes panel-in {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </>
  );
}

function ActivityCell({
  Icon,
  tint,
  label,
  sub,
  n,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  tint: string;
  label: string;
  sub: string;
  n: number;
}) {
  return (
    <div className="rounded-md bg-slate-900/60 border border-slate-800 px-2 py-1.5">
      <div className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${tint}`} />
        <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
          {label}
        </span>
      </div>
      <div className="text-base font-bold text-white tabular-nums leading-none mt-0.5">
        {n.toLocaleString()}
      </div>
      <div className="text-[9px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

/* ─── Under the hood ─────────────────────────────────────────────── */

/**
 * Collapsible "Under the hood" section. Communicates that this is a
 * sophisticated technical platform without naming vendors, model
 * providers, or specific data sources. Each row is one capability
 * + a one-line plain-English description aimed at a casual reader.
 *
 * Default-closed so the dashboard remains focused on the live data;
 * expanding is opt-in for the curious user.
 */
function UnderTheHood() {
  const [open, setOpen] = useState(false);
  const items: Array<{
    Icon: React.ComponentType<{ className?: string }>;
    tint: string;
    title: string;
    sub: string;
  }> = [
    {
      Icon: Radio,
      tint: 'text-cyan-300',
      title: 'Real-time vessel ingestion',
      sub: 'Streaming positions normalised, deduplicated, and indexed within seconds.',
    },
    {
      Icon: Globe2,
      tint: 'text-blue-300',
      title: 'Globally distributed compute',
      sub: 'Edge-resident services keep the map and inspections snappy from anywhere on the bridge.',
    },
    {
      Icon: Layers,
      tint: 'text-emerald-300',
      title: 'Geospatial intelligence layer',
      sub: 'Bounding-box, route-corridor, and proximity queries over millions of positions.',
    },
    {
      Icon: Cpu,
      tint: 'text-amber-300',
      title: 'AI-augmented authoring',
      sub: 'Private inference drafts inspection reports — your data is never used to train models.',
    },
    {
      Icon: Sparkles,
      tint: 'text-violet-300',
      title: 'Semantic standards engine',
      sub: 'Vector-indexed regulatory text auto-cites SOLAS, MARPOL, ISM, ISPS, MLC against every observation.',
    },
    {
      Icon: Database,
      tint: 'text-cyan-300',
      title: 'Audit-grade record store',
      sub: 'Versioned, append-only inspection history with cryptographic signatures on every signed report.',
    },
  ];

  return (
    <div className="border-t border-slate-800/80">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full px-4 py-2 flex items-center gap-2 text-left hover:bg-slate-900/60 transition-colors"
      >
        <Layers className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
        <span className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">
          Under the hood
        </span>
        <span className="text-[10px] text-slate-500 ml-auto">
          {open ? 'Hide' : 'What\'s running here'}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="px-4 pb-3 pt-1 space-y-2 animate-[panel-in_180ms_ease-out]">
          {items.map((it) => (
            <div key={it.title} className="flex items-start gap-2.5">
              <div className="shrink-0 w-7 h-7 rounded-md bg-slate-900/80 border border-slate-800 flex items-center justify-center mt-0.5">
                <it.Icon className={`w-3.5 h-3.5 ${it.tint}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold text-slate-100 leading-tight">{it.title}</div>
                <div className="text-[10.5px] text-slate-400 leading-snug mt-0.5">{it.sub}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
